import { IconButton, Stack, useTheme } from "@mui/material";
import { Tooltip } from "components";
import {
  CheckIcon,
  CopyIcon,
  // SyncIcon,
  ThumbsDownIcon,
  ThumbsUpIcon,
} from "components/icons";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import type { AskHelenaMessageFeedback } from "shared";
import type { AskHelenaRateMessage } from "../hooks/useAskHelenaMessageFeedback";

export const ASK_HELENA_MESSAGE_ACTIONS_CLASS = "ask-helena-message-actions";

const COPIED_RESET_MS = 2000;

interface AskHelenaMessageActionsProps {
  content: string;
  messageId: string | null | undefined;
  feedback: AskHelenaMessageFeedback | null | undefined;
  onRate: AskHelenaRateMessage;
  showRegenerate: boolean;
  isAlwaysVisible: boolean;
}

export const AskHelenaMessageActions = memo(
  ({
    content,
    messageId,
    feedback,
    onRate,
    showRegenerate,
    isAlwaysVisible,
  }: AskHelenaMessageActionsProps) => {
    const theme = useTheme();
    const [isCopied, setIsCopied] = useState(false);
    const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const rating = feedback?.rating ?? null;

    useEffect(
      () => () => {
        if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
      },
      [],
    );

    const handleCopy = useCallback(async () => {
      try {
        await navigator.clipboard.writeText(content);
        setIsCopied(true);
        if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
        copiedTimerRef.current = setTimeout(
          () => setIsCopied(false),
          COPIED_RESET_MS,
        );
      } catch {
        setIsCopied(false);
      }
    }, [content]);

    const buttonSx = {
      p: "0px",
      fontSize: "16px",
      borderRadius: "4px",
      color: theme.palette.lightBg.medium,
      ":hover": {
        color: theme.palette.lightBg.high,
        backgroundColor: "transparent",
      },
    };

    const iconSx = { fontSize: "16px" };

    return (
      <Stack
        className={ASK_HELENA_MESSAGE_ACTIONS_CLASS}
        direction="row"
        sx={{
          alignItems: "center",
          gap: "8px",
          opacity: isAlwaysVisible ? 1 : 0,
          pointerEvents: isAlwaysVisible ? "auto" : "none",
          transition: theme.transitions.create("opacity", {
            duration: theme.transitions.duration.shortest,
          }),
        }}
      >
        <Tooltip title={isCopied ? "Copied" : "Copy response"} placement="top">
          <IconButton
            aria-label="Copy message"
            onClick={handleCopy}
            sx={buttonSx}
          >
            {isCopied ? (
              <CheckIcon
                sx={{ ...iconSx, color: theme.palette.success.main }}
              />
            ) : (
              <CopyIcon sx={iconSx} />
            )}
          </IconButton>
        </Tooltip>

        {messageId ? (
          <>
            <Tooltip title="Good response" placement="top">
              <IconButton
                aria-label="Good response"
                aria-pressed={rating === "up"}
                onClick={() => onRate(messageId, "up")}
                sx={{
                  ...buttonSx,
                  ...(rating === "up" && { color: theme.palette.primary.main }),
                }}
              >
                <ThumbsUpIcon sx={iconSx} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Bad response" placement="top">
              <IconButton
                aria-label="Bad response"
                aria-pressed={rating === "down"}
                onClick={() => onRate(messageId, "down")}
                sx={{
                  ...buttonSx,
                  ...(rating === "down" && {
                    color: theme.palette.primary.main,
                  }),
                }}
              >
                <ThumbsDownIcon sx={iconSx} />
              </IconButton>
            </Tooltip>
          </>
        ) : null}

        {/* {showRegenerate ? (
          <Tooltip title="Regenerate" placement="top">
            <IconButton aria-label="Regenerate response" sx={buttonSx}>
              <SyncIcon sx={iconSx} />
            </IconButton>
          </Tooltip>
        ) : null} */}
      </Stack>
    );
  },
);

AskHelenaMessageActions.displayName = "AskHelenaMessageActions";
